import { Field, FieldProps, Formik, FormikProps } from 'formik';
import { FormattedMessage } from 'react-intl';
import { Grid } from '@mui/material';
import {
    PoolButton,
    PoolModal,
    PoolSelect,
    PoolTextField,
    ScrollToFieldError,
    SelectOption,
} from '@synergy/components';
import { SystemNotification, SystemNotificationType } from '@synergy/admin/app-settings/data-access';
import { fields } from '@synergy/utils';

import { notificationModalValidationSchema } from './notification-modal-validation-schema';

interface AddressModalProps {
    notification: SystemNotification | null;
    onClose: () => void;
    onNotificationSubmit: (values: SystemNotification) => void;
}

export const NotificationsModal = (props: AddressModalProps) => {
    const { notification, onClose, onNotificationSubmit } = props;

    const hasNotificationId = notification?.id && notification.id > 0;
    const fieldNames = fields<SystemNotification>();

    const typeOptions = Object.values(SystemNotificationType).map<SelectOption>(type => ({
        value: type,
        displayValue: <FormattedMessage id={`app.enum.systemNotificationType.${type}`} />,
    }));

    const handleNotificationTypeChange = (form: FormikProps<SystemNotification>, name: string) => (value?: string) => {
        if (value) {
            form.setFieldValue(name, value);
        }
    };

    const getActionChildren = (formikProps: FormikProps<SystemNotification>) => {
        return [
            <PoolButton key="1" styleType="firstLevel" variant="text" onClick={onClose}>
                <FormattedMessage id="app.common.cancel" />
            </PoolButton>,
            <PoolButton key="2" styleType="firstLevel" type="submit" onClick={() => formikProps.submitForm()}>
                <FormattedMessage id={hasNotificationId ? 'app.common.save' : 'app.common.add'} />
            </PoolButton>,
        ];
    };

    if (!notification) {
        return null;
    }

    return (
        <Formik
            initialValues={notification}
            onSubmit={onNotificationSubmit}
            validationSchema={notificationModalValidationSchema}
        >
            {formikProps => (
                <PoolModal
                    title={
                        <FormattedMessage
                            id={
                                hasNotificationId
                                    ? 'app.settings.notification.editNotification'
                                    : 'app.settings.notification.addNotification'
                            }
                        />
                    }
                    isVisible={!!notification}
                    actionChildren={getActionChildren(formikProps)}
                    handleClose={onClose}
                >
                    <ScrollToFieldError />
                    <Grid container={true} spacing={5}>
                        <Grid container={true} item={true} spacing={2}>
                            <Grid item={true} xs={12}>
                                <Field name={fieldNames.type}>
                                    {({ field, form }: FieldProps<SystemNotificationType>) => (
                                        <PoolSelect
                                            fullWidth={true}
                                            options={typeOptions}
                                            disallowDefaultValue={true}
                                            label={<FormattedMessage id={'app.label.type'} />}
                                            onValueChange={handleNotificationTypeChange(form, field.name)}
                                            value={field.value}
                                            name={field.name}
                                            noSorting={true}
                                        />
                                    )}
                                </Field>
                            </Grid>
                            <Grid item={true} xs={12}>
                                <Field name={fieldNames.email}>
                                    {({ field, meta }: FieldProps<string>) => (
                                        <PoolTextField
                                            label={<FormattedMessage id="app.common.emailTo" />}
                                            fullWidth={true}
                                            error={!!meta.touched && !!meta.error}
                                            helperText={
                                                meta.touched && meta.error ? <FormattedMessage id={meta.error} /> : null
                                            }
                                            required
                                            {...field}
                                        />
                                    )}
                                </Field>
                            </Grid>
                        </Grid>
                    </Grid>
                </PoolModal>
            )}
        </Formik>
    );
};
